import React, { useCallback, useRef, useState } from 'react';
import { Button, Dialog } from '@iqueue/iq-ui';

/**
 * Подтверждение разрушительного действия — `role="alertdialog"`, как в рецепте
 * «Settings screen»: глиф, заголовок с ИМЕНЕМ того, что исчезнет, описание
 * последствий и две кнопки. Отмена стоит первой и обычной, подтверждение —
 * `tone="error"`: цвет несёт смысл, а не украшает.
 *
 * Нативный `confirm()` заменён именно на это: он рисуется браузером вне темы,
 * блокирует поток и на мобильных выглядит чужеродно, а главное — не может
 * назвать объект и последствия так, как это делает диалог дома.
 */
export function ConfirmDialog({ title, description, confirmLabel = 'Удалить', icon = 'delete_forever', onDecide }) {
  return (
    <Dialog.Root role="alertdialog" open onOpenChange={(open) => { if (!open) onDecide(false); }}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Icon name={icon} />
          <Dialog.Title>{title}</Dialog.Title>
          {description && <Dialog.Description>{description}</Dialog.Description>}
          <Dialog.Actions>
            <Button variant="text" onClick={() => onDecide(false)}>Отмена</Button>
            <Button variant="text" tone="error" onClick={() => onDecide(true)}>{confirmLabel}</Button>
          </Dialog.Actions>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * `const [confirmDialog, confirm] = useConfirm()` — рендерим `confirmDialog`
 * рядом с прочими диалогами, а в обработчике пишем
 * `if (!(await confirm({ title, description }))) return;`.
 *
 * Один экземпляр на экран, а не по диалогу на строку: фокусу нужно постоянное
 * место возврата, а ответ приходит промисом, поэтому место вызова остаётся
 * линейным и не расползается по колбэкам.
 */
export function useConfirm() {
  const [request, setRequest] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolveRef.current = resolve;
    setRequest(opts);
  }), []);

  const decide = useCallback((ok) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setRequest(null);
    if (resolve) resolve(ok);
  }, []);

  const dialog = request ? <ConfirmDialog {...request} onDecide={decide} /> : null;
  return [dialog, confirm];
}
